/**

 * @vuelto-surface

 * mobile: ok

 * desktop: ok

 * strategy: inherit

 */

import React from 'react';

import { StyleSheet, Switch, Text, TouchableOpacity, View, type ViewStyle } from 'react-native';

import { Ionicons } from '@expo/vector-icons';

import { SURFACE_CARD_RADIUS_CONTENT } from '../../shared/surface-cards/surfaceCards.styles';

import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';

import type { PromotionWithTargets } from '../domain/promotionTypes';

import { formatPromotionBenefit, formatPromotionValidity, PROMOTION_APPLY_MODE_LABELS, PROMOTION_KIND_LABELS, PROMOTION_SCOPE_LABELS, promotionStatusLabel, promotionStatusTone } from '../application/promotionPresentation'

import type { PromotionSortDir, PromotionSortKey } from '../application/usePromotionsListScreen';
import { formatMoney } from '../../shared/format/public-api';



type Props = {

  promotions: PromotionWithTargets[];

  canEdit: boolean;

  canActivate?: boolean;

  onEdit: (promotionId: string) => void;

  onToggleActive: (promo: PromotionWithTargets) => void;

  onDuplicate: (promo: PromotionWithTargets) => void;

  onOpenHistory: (promo: PromotionWithTargets) => void;

  sortKey: PromotionSortKey;

  sortDir: PromotionSortDir;

  onSortChange: (key: PromotionSortKey) => void;

  performanceByPromoId: Map<string, { usesCount: number; discountTotal: number }>;

  batchMode?: boolean;

  selectedIds?: Set<string>;

  onToggleSelection?: (id: string) => void;

  onToggleSelectAll?: () => void;

  /** Dentro del panel desk: sin borde/radio propio. */

  embedded?: boolean;

};



function mapStatusTone(tone: ReturnType<typeof promotionStatusTone>): 'ok' | 'warn' | 'pause' {

  if (tone === 'success') return 'ok';

  if (tone === 'warning') return 'warn';

  return 'pause';

}



function SortableHeader({

  label,

  sortKeyForCol,

  sortKey,

  sortDir,

  onSortChange,

  style,

}: {

  label: string;

  sortKeyForCol: PromotionSortKey;

  sortKey: PromotionSortKey;

  sortDir: PromotionSortDir;

  onSortChange: (key: PromotionSortKey) => void;

  style: ViewStyle;

}) {

  const isActive = sortKey === sortKeyForCol;

  return (

    <TouchableOpacity

      style={[style, styles.headerCellTap]}

      onPress={() => onSortChange(sortKeyForCol)}

      accessibilityRole="button"

      accessibilityLabel={`Ordenar por ${label}`}

    >

      <Text

        style={[

          styles.headerCell,

          isActive && styles.headerCellActive,

        ]}

        numberOfLines={1}

      >

        {label}{isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}

      </Text>

    </TouchableOpacity>

  );

}



export function PromotionsTable({

  promotions,

  canEdit,

  canActivate = true,

  onEdit,

  onToggleActive,

  onDuplicate,

  onOpenHistory,

  sortKey,

  sortDir,

  onSortChange,

  performanceByPromoId,

  batchMode = false,

  selectedIds,

  onToggleSelection,

  onToggleSelectAll,

  embedded = false,

}: Props) {

  if (promotions.length === 0) return null;

  const allSelected = batchMode && selectedIds != null && selectedIds.size === promotions.length && promotions.length > 0;



  return (

    <View style={[styles.tableOuter, embedded && styles.tableOuterEmbedded]} testID="promotions.table">

      <View style={[styles.table, embedded && styles.tableEmbedded]}>

        <View style={[styles.row, styles.headerRow]}>

          {batchMode ? (

            <TouchableOpacity

              style={styles.selectCell}

              onPress={() => onToggleSelectAll?.()}

              accessibilityRole="checkbox"

              accessibilityLabel="Seleccionar todo"

              testID="promotions.table.select-all"

            >

              <Ionicons

                name={allSelected ? 'checkbox-outline' : 'square-outline'}

                size={20}

                color={allSelected ? ticketColors.gold : ticketColors.ink2}

              />

            </TouchableOpacity>

          ) : null}

          <SortableHeader label="Nombre" sortKeyForCol="name" sortKey={sortKey} sortDir={sortDir} onSortChange={onSortChange} style={styles.nameCell} />

          <Text style={[styles.headerCell, styles.benefitCell]}>Beneficio</Text>

          <SortableHeader label="Tipo" sortKeyForCol="kind" sortKey={sortKey} sortDir={sortDir} onSortChange={onSortChange} style={styles.kindCell} />

          <SortableHeader label="Alcance" sortKeyForCol="scope" sortKey={sortKey} sortDir={sortDir} onSortChange={onSortChange} style={styles.scopeCell} />

          <Text style={[styles.headerCell, styles.modeCell]}>Modo</Text>

          <SortableHeader label="Estado" sortKeyForCol="status" sortKey={sortKey} sortDir={sortDir} onSortChange={onSortChange} style={styles.statusCell} />

          <SortableHeader label="Código" sortKeyForCol="code" sortKey={sortKey} sortDir={sortDir} onSortChange={onSortChange} style={styles.codeCell} />

          <Text style={[styles.headerCell, styles.perfCell]}>Usos (30 días)</Text>

          <Text style={[styles.headerCell, styles.actionsCell]}>Acciones</Text>

        </View>

        {promotions.map((promo) => {

          const statusLabel = promotionStatusLabel(promo);

          const statusTone = mapStatusTone(promotionStatusTone(promo));

          const code = promo.promoCode?.trim() || '—';



          return (

            <View key={promo.id} style={styles.row}>

              {batchMode ? (

                <TouchableOpacity

                  style={styles.selectCell}

                  onPress={() => onToggleSelection?.(promo.id)}

                  accessibilityRole="checkbox"

                  accessibilityLabel={`Seleccionar ${promo.name}`}

                  testID={`promotions.table.select.${promo.id}`}

                >

                  <Ionicons

                    name={selectedIds?.has(promo.id) ? 'checkbox-outline' : 'square-outline'}

                    size={20}

                    color={selectedIds?.has(promo.id) ? ticketColors.gold : ticketColors.ink2}

                  />

                </TouchableOpacity>

              ) : null}

              <TouchableOpacity

                style={styles.nameCell}

                onPress={() => onEdit(promo.id)}

                activeOpacity={0.85}

                accessibilityRole="button"

                accessibilityLabel={`Editar promoción ${promo.name}`}

              >

                <Text style={[styles.cell, styles.nameText]} numberOfLines={1}>

                  {promo.name}

                </Text>

              </TouchableOpacity>

              <Text style={[styles.cell, styles.benefitCell]} numberOfLines={1}>

                {formatPromotionBenefit(promo)}

              </Text>

              <Text style={[styles.cell, styles.kindCell]} numberOfLines={1}>

                {PROMOTION_KIND_LABELS[promo.kind]}

              </Text>

              <Text style={[styles.cell, styles.scopeCell]} numberOfLines={1}>

                {PROMOTION_SCOPE_LABELS[promo.scope]}

              </Text>

              <Text style={[styles.cell, styles.modeCell]} numberOfLines={1}>

                {PROMOTION_APPLY_MODE_LABELS[promo.applyMode]}

              </Text>

              <View style={styles.statusCell}>

                <Text

                  style={[

                    styles.statusText,

                    statusTone === 'ok' && styles.statusOk,

                    statusTone === 'warn' && styles.statusWarn,

                    statusTone === 'pause' && styles.statusPause,

                  ]}

                  numberOfLines={1}

                >

                  {statusLabel}

                </Text>

                {(() => {

                  const validity = formatPromotionValidity(promo);

                  if (!validity) return null;

                  const isExpired = validity.startsWith('Vencida');

                  const isWarning = isExpired || validity.startsWith('Vence');

                  return (

                    <Text

                      style={[

                        styles.validityBadge,

                        isExpired && styles.validityBadgeExpired,

                        isWarning && !isExpired && styles.validityBadgeWarning,

                      ]}

                      numberOfLines={1}

                    >

                      {validity}

                    </Text>

                  );

                })()}

              </View>

              <Text style={[styles.cell, styles.codeCell, styles.codeText]} numberOfLines={1}>

                {code}

              </Text>

              <View style={styles.perfCell}>

                {(() => {

                  const perf = performanceByPromoId.get(promo.id);

                  if (!perf) return <Text style={styles.cell}>—</Text>;

                  return (

                    <View style={styles.perfCol}>

                      <Text style={styles.perfUses}>{perf.usesCount}</Text>

                      <Text style={styles.perfDiscount}>

                        {formatMoney(perf.discountTotal)}

                      </Text>

                    </View>

                  );

                })()}

              </View>

              <View style={[styles.actionsCell, styles.actionsRow]}>

                <TouchableOpacity onPress={() => onOpenHistory(promo)} accessibilityRole="button">

                  <Text style={styles.link}>Historial</Text>

                </TouchableOpacity>

                {canEdit ? (

                  <TouchableOpacity onPress={() => void onDuplicate(promo)} accessibilityRole="button">

                    <Text style={styles.link}>Duplicar</Text>

                  </TouchableOpacity>

                ) : null}

                {canEdit ? (

                  <Switch

                    value={promo.isActive}

                    onValueChange={canActivate ? () => void onToggleActive(promo) : undefined}

                    disabled={!canActivate}

                    accessibilityLabel={`Activar ${promo.name}`}

                  />

                ) : null}

              </View>

            </View>

          );

        })}

      </View>

    </View>

  );

}



const styles = StyleSheet.create({

  tableOuter: {

    width: '100%',

    maxWidth: '100%',

    alignSelf: 'stretch',

    marginBottom: spacing.sm,

  },

  tableOuterEmbedded: {

    marginBottom: 0,

    flex: 1,

  },

  selectCell: {

    width: 36,

    alignItems: 'center',

    justifyContent: 'center',

    paddingVertical: spacing.xs,

  },

  table: {

    width: '100%',

    borderRadius: SURFACE_CARD_RADIUS_CONTENT,

    borderBottomLeftRadius: 0,

    borderBottomRightRadius: 0,

    borderWidth: 1,

    borderColor: ticketColors.ruleSoft,

    backgroundColor: ticketColors.paper2,

    overflow: 'hidden',

  },

  tableEmbedded: {

    borderWidth: 0,

    borderRadius: 0,

    borderBottomLeftRadius: 0,

    borderBottomRightRadius: 0,

  },

  row: {

    flexDirection: 'row',

    alignItems: 'center',

    minHeight: 52,

    paddingVertical: 12,

    paddingLeft: 14,

    paddingRight: 14,

    borderBottomWidth: StyleSheet.hairlineWidth,

    borderBottomColor: ticketColors.ruleSoft,

    gap: spacing.sm,

  },

  headerRow: {

    minHeight: 40,

    paddingVertical: 10,

    paddingLeft: 14,

    paddingRight: 14,

    backgroundColor: ticketColors.paper2,

    borderBottomColor: ticketColors.rule,

  },

  headerCell: {

    fontSize: 10,

    color: ticketColors.ink3,

    fontWeight: '700',

    textTransform: 'uppercase',

    letterSpacing: 0.8,

    fontFamily: ticketFonts.sans,

  },

  cell: {

    fontSize: 13,

    color: ticketColors.ink,

    fontFamily: ticketFonts.sans,

  },

  nameCell: { flex: 1.6, minWidth: 120 },

  nameText: {

    fontSize: 14,

    fontWeight: '600',

  },

  benefitCell: { flex: 0.9, minWidth: 70 },

  kindCell: { flex: 1.2, minWidth: 100 },

  scopeCell: { flex: 1, minWidth: 80 },

  modeCell: { flex: 1.1, minWidth: 90 },

  statusCell: { flex: 0.9, minWidth: 88, justifyContent: 'center', gap: 2 },

  codeCell: { flex: 0.8, minWidth: 70 },

  codeText: {

    fontFamily: ticketFonts.mono,

    fontVariant: ['tabular-nums'],

    letterSpacing: 0.2,

    color: ticketColors.ink3,

    fontWeight: '600',

  },

  actionsCell: { flexShrink: 0, minWidth: 140, maxWidth: 200 },

  actionsRow: {

    flexDirection: 'row',

    flexWrap: 'wrap',

    alignItems: 'center',

    gap: spacing.sm,

  },

  link: {

    fontSize: 12,

    color: ticketColors.gold,

    fontWeight: '600',

    fontFamily: ticketFonts.sans,

  },

  headerCellTap: {

    minWidth: 0,

  },

  headerCellActive: {

    color: ticketColors.gold,

  },

  statusText: {

    fontSize: 11,

    fontWeight: '600',

    letterSpacing: 0.4,

    textTransform: 'uppercase',

    color: ticketColors.ink3,

    fontFamily: ticketFonts.sans,

  },

  statusOk: {

    color: ticketColors.ok,

  },

  statusWarn: {

    color: ticketColors.gold,

  },

  statusPause: {

    color: ticketColors.ink3,

  },

  validityBadge: {

    fontSize: 10,

    fontWeight: '600',

    color: ticketColors.ink3,

    flexShrink: 0,

    fontFamily: ticketFonts.sans,

  },

  validityBadgeExpired: {

    color: ticketColors.gold,

  },

  validityBadgeWarning: {

    color: ticketColors.gold,

  },

  perfCell: { flex: 0.7, minWidth: 60 },

  perfCol: {

    gap: 0,

  },

  perfUses: {

    fontSize: 13,

    fontWeight: '700',

    color: ticketColors.ink,

    fontFamily: ticketFonts.mono,

    fontVariant: ['tabular-nums'],

  },

  perfDiscount: {

    fontSize: 10,

    color: ticketColors.ink3,

    fontFamily: ticketFonts.mono,

    fontVariant: ['tabular-nums'],

  },

});


